import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dimensions,
  Image,
  Platform,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  hasLowerCase,
  hasNumber,
  hasUpperCase,
  hasWhiteSpace,
  isEightChars,
} from "../modules/stringValidators";
import bulletIcon from "./assets/icons/icon_bullet_small.png";
import crossIcon from "./assets/icons/icon_close_small.png";
import tickIcon from "./assets/icons/icon_tick_small.png";
import colours from "./styles/colours";
import { Spacing } from "./styles/number";
import { Typography } from "./styles/typography";

interface Input {
  text: string;
  error: boolean;
}

export interface PasswordProps {
  value?: string;
  passwordInput?: Input;
  isError?: boolean;
}

const styles = StyleSheet.create({
  innerCell: {
    flexDirection: "row",
    alignItems: "center",
    width: Platform.OS === "web" ? "100%" : Dimensions.get("window").width / 2,
    marginRight: Platform.OS === "web" ? "12em" : 0,
  },
  cellContainer: {
    marginTop: -Spacing.Thin,
    flexDirection: "row",
    marginRight: Spacing.Light,
    justifyContent: "flex-start",
    alignContent: "center",
    marginBottom: Spacing.Regular,
  },
  icon: {
    width: 24,
    height: 24,
  },
  passwordDesc: {
    ...Typography.bodyTwo,
    color: colours.midGrey,
  },
});

const PasswordValidation: React.FC<PasswordProps> = ({
  value,
  passwordInput,
  isError,
}) => {
  const { t } = useTranslation();

  const passwordValue = value ?? "";
  const validators = {
    isEightChars: isEightChars(passwordValue),
    hasLowerCase: hasLowerCase(passwordValue),
    hasNumber: hasNumber(passwordValue),
    hasUpperCase: hasUpperCase(passwordValue),
    hasWhiteSpace: hasWhiteSpace(passwordValue),
  };

  const renderIcon = (iconState: boolean) => {
    const icon = iconState
      ? tickIcon
      : passwordInput?.error || isError
      ? crossIcon
      : bulletIcon;
    return (
      <Image
        style={styles.icon}
        // inline icon
        source={icon}
      />
    );
  };

  const renderInnerCell = (text: string, iconState: boolean) => {
    return (
      <View style={styles.innerCell}>
        {renderIcon(iconState)}
        <Text style={styles.passwordDesc}>{text}</Text>
      </View>
    );
  };

  return (
    <View style={styles.cellContainer}>
      <View>
        {renderInnerCell(
          t("LoginRegistration:passwordDescOne"),
          validators.isEightChars
        )}
        {renderInnerCell(
          t("LoginRegistration:passwordDescTwo"),
          validators.hasLowerCase
        )}
      </View>
      <View>
        {renderInnerCell(
          t("LoginRegistration:passwordDescThree"),
          validators.hasNumber
        )}
        {renderInnerCell(
          t("LoginRegistration:passwordDescFour"),
          validators.hasUpperCase
        )}
        {Platform.OS !== "web" &&
          renderInnerCell(
            t("LoginRegistration:passwordDescFive"),
            !validators.hasWhiteSpace
          )}
      </View>

      {Platform.OS === "web" && (
        <View>
          {renderInnerCell(
            t("LoginRegistration:passwordDescFive"),
            !validators.hasWhiteSpace
          )}
        </View>
      )}
    </View>
  );
};

export default PasswordValidation;
